/**
 * Subtitle region for hardsub OCR: the rectangle of the video frame where the
 * burned-in subtitles live. Shared by the picker (client), the create
 * endpoint (validation) and - through relative imports - the platform worker
 * and container, so every side agrees on the coordinate space.
 *
 * Coordinates are normalized to 0..1 of the DISPLAYED frame (after rotation
 * metadata and sample aspect ratio), which is what the browser reports as
 * videoWidth/videoHeight and what ffmpeg decodes with autorotate.
 */

export type SubtitleRegion = {
  x: number;
  y: number;
  w: number;
  h: number;
};

/** "vertical" = top-to-bottom CJK subtitles running in columns. */
export type SubtitleOrientation = "horizontal" | "vertical";

/** Anything but an explicit "vertical" is a regular horizontal job. */
export const parseSubtitleOrientation = (value: unknown): SubtitleOrientation =>
  value === "vertical" ? "vertical" : "horizontal";

/** Bottom band where most subtitles sit; the picker starts from this. */
export const DEFAULT_SUBTITLE_REGION: SubtitleRegion = { x: 0.05, y: 0.74, w: 0.9, h: 0.22 };

/** Right-hand band where vertical subtitles usually sit. */
export const VERTICAL_SUBTITLE_REGION: SubtitleRegion = { x: 0.76, y: 0.05, w: 0.2, h: 0.8 };

export const defaultSubtitleRegion = (orientation: SubtitleOrientation): SubtitleRegion =>
  orientation === "vertical" ? VERTICAL_SUBTITLE_REGION : DEFAULT_SUBTITLE_REGION;

/** Smallest rectangle worth reading (fractions of the frame). */
export const MIN_REGION_W = 0.03;
export const MIN_REGION_H = 0.03;

/**
 * Longest media an OCR job accepts without a clip range. Decoding every frame
 * is CPU-bound in the container; longer videos must pick a range.
 */
export const HARDSUB_MAX_SECONDS = 2 * 60 * 60;

/** Frames sampled per second of video. Timing error is ±1/(2·fps). */
export const HARDSUB_SAMPLE_FPS = 2;

export type SubtitleRegionResult =
  | { ok: true; region: SubtitleRegion }
  | { ok: false; code: "REGION_INVALID"; message: string };

const toFraction = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const round4 = (n: number) => Math.round(n * 10000) / 10000;

/**
 * Clamps a client-supplied rectangle into the frame and rejects one that is
 * missing, outside the frame or too small to hold a line of text.
 */
export const normalizeSubtitleRegion = (input: unknown): SubtitleRegionResult => {
  const raw = (input ?? null) as Partial<Record<keyof SubtitleRegion, unknown>> | null;
  const x = toFraction(raw?.x);
  const y = toFraction(raw?.y);
  const w = toFraction(raw?.w);
  const h = toFraction(raw?.h);
  if (x === null || y === null || w === null || h === null) {
    return { ok: false, code: "REGION_INVALID", message: "The subtitle area is missing or malformed" };
  }

  const left = Math.min(1, Math.max(0, x));
  const top = Math.min(1, Math.max(0, y));
  const right = Math.min(1, Math.max(left, x + w));
  const bottom = Math.min(1, Math.max(top, y + h));
  const width = round4(right - left);
  const height = round4(bottom - top);

  if (width < MIN_REGION_W || height < MIN_REGION_H) {
    return {
      ok: false,
      code: "REGION_INVALID",
      message: "The subtitle area is too small; drag a rectangle around the subtitle line",
    };
  }

  return { ok: true, region: { x: round4(left), y: round4(top), w: width, h: height } };
};

export type PixelRect = { x: number; y: number; w: number; h: number };

/**
 * Normalized region → even-sized pixel rectangle inside a frame of the given
 * size. Even sizes keep chroma-subsampled crops valid in ffmpeg; the same
 * math drives the picker's crop preview and the container's crop filter.
 */
export const regionToPixels = (region: SubtitleRegion, frameWidth: number, frameHeight: number): PixelRect => {
  const fw = Math.max(0, Math.floor(frameWidth));
  const fh = Math.max(0, Math.floor(frameHeight));
  const even = (n: number) => Math.max(0, Math.floor(n / 2) * 2);
  const x = even(region.x * fw);
  const y = even(region.y * fh);
  const w = Math.min(even(region.w * fw), even(fw - x));
  const h = Math.min(even(region.h * fh), even(fh - y));
  return { x, y, w, h };
};
